

import React, { memo, useMemo, useState, useEffect } from 'react';
import SVG from 'react-inlinesvg';
import cx from 'classnames';
import { DEFAULT_OG_TAGS } from '@shootsta/common-domain-data';
import { UploaderConsumer } from '../../Uploader';
import { DataTable, EmptyDataPlaceholder, Text, FormModal } from '../../../ui-kit';
import empty from '../../../assets/images/Elevate-blank.svg';
import { getTemplatesDataTableColumns } from '../utils';

import SplashDetails from './common/SplashDetails';
import ArchiveSplashTemplate from './common/ArchiveSplashTemplate';
import ManageSocialSettings from './common/ManageSocialSettings';
import LinkElevateTemplates from './common/LinkElevateTemplates';

import '../styles/_splash-page-list.scss';

const STATUS = Object.freeze({
  PUBLISHED: 'PUBLISHED',
  UNPUBLISHED: 'UNPUBLISHED',
  ARCHIVE: 'ARCHIVED',
  UNARCHIVE: 'UNARCHIVED',
  CLONE: 'CLONE',
  UPDATE: 'UPDATE'
});

























const NoDataPlaceholder = ({ orgName }) =>
<div className="list__no-data-container">
    <div className="list__no-data__svg-container">
      <SVG cacheRequests src={empty} uniquifyIDs title="There's nothing here yet." />
    </div>
    <Text heading3 centered noMargin="top">{'There\'s nothing here yet but we\'re glad you are!'}</Text>
    <Text noMargin centered placeholder>
      Create, manage and publish templates to be used
      {' '}
      {orgName && `by ${orgName}`}
    </Text>
  </div>;


const SplashTemplateList = (props) => {
  const {
    isLoading,
    templates,
    search,
    orgName,
    updateTemplate,
    setTemplateDetails,
    navigateToEditTemplate,
    getTemplatesByOrg,
    getSplashRelations,
    updatePitchSplashLink,
    getElevateTemplatesByOrg,
    workspaceSocialSettings = DEFAULT_OG_TAGS,
    organisationId,
    updateAsset,
    getPublicAssetUrl,
    isInternalAdmin,
    isMobile
  } = props;

  const [isPublishVisible, setPublishVisible] = useState(false);
  const [isUnpublishVisible, setUnpublishVisible] = useState(false);
  const [templateId, setTemplateId] = useState(null);
  const [isListLoading, setListLoading] = useState(isLoading);
  const [showCloneModal, setShowCloneModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [editDetails, setEditDetails] = useState(null);
  const [showArchiveSplashModal, setShowArchiveSplashModal] = useState(false);
  const [allSplashRelations, setAllSplashRelations] = useState([]);
  const [showManageSettingsModal, setShowManageSettingsModal] = useState(false);
  const [showElevateLinkModal, setShowElevateLinkModal] = useState(false);

  useEffect(() => {
    setListLoading(isLoading);
  }, [isLoading]);

  const onArchiveSave = async (selectedTemplateId) => {
    const input = { templateId: selectedTemplateId, status: STATUS.ARCHIVE };

    setListLoading(true);
    await updateTemplate({ templateId: input.templateId, action: input.status });
    await setTemplateDetails();
    setListLoading(false);
  };

  const onArchive = async (selectedTemplateId) => {
    setTemplateId(selectedTemplateId);

    const { data, error } = await getSplashRelations(selectedTemplateId);

    if (error || !data) {
      return;
    }

    if (data.getSplashTemplateRelations.length) {
      setAllSplashRelations(data.getSplashTemplateRelations);
      setShowArchiveSplashModal(true);
    } else {
      onArchiveSave(selectedTemplateId);
    }
  };


  const onUnarchive = async (selectedTemplateId) => {
    const input = {
      templateId: selectedTemplateId,
      status: STATUS.UNARCHIVE
    };

    setListLoading(true);
    await updateTemplate({ templateId: input.templateId, action: input.status });
    await setTemplateDetails();
    setListLoading(false);
  };

  const onClone = (selectedTemplateId) => {
    setTemplateId(selectedTemplateId);
    setShowCloneModal(true);
  };

  const onPublish = (selectedTemplateId) => {
    setPublishVisible(true);
    setTemplateId(selectedTemplateId);
  };

  const onPublishModalClose = () => {
    setPublishVisible(false);
    setTemplateId(null);
  };

  const onPublishSave = async () => {
    const input = { templateId, status: STATUS.PUBLISHED };
    setListLoading(true);
    await updateTemplate({ templateId: input.templateId, action: input.status });
    await setTemplateDetails();
    setListLoading(false);
  };

  const onUnPublish = async (selectedTemplateId) => {
    setUnpublishVisible(true);
    setTemplateId(selectedTemplateId);
  };

  const onArchiveModalClose = () => {
    setShowArchiveSplashModal(false);
    setTemplateId(null);
    setAllSplashRelations([]);
  };

  const onUnpublishModalClose = () => {
    setUnpublishVisible(false);
    setTemplateId(null);
  };

  const onUnpublishSave = async () => {
    const input = { templateId, status: STATUS.UNPUBLISHED };
    setListLoading(true);
    await updateTemplate({ templateId: input.templateId, action: input.status });
    await setTemplateDetails();
    setListLoading(false);
  };

  const onCloneModalClose = () => setShowCloneModal(false);

  const onCloneTemplate = async () => {
    const input = { templateId, status: STATUS.CLONE };
    setListLoading(true);
    await updateTemplate({ templateId: input.templateId, action: input.status });
    await setTemplateDetails();
    setListLoading(false);
  };

  const onDetailsModal = (selectedTemplateId) => {
    const templateDetails = templates && templates.find(
      (template) => template.templateId === selectedTemplateId
    );
    setEditDetails(templateDetails);
    setShowDetailsModal(true);
    setTemplateId(selectedTemplateId);
  };

  const onDetailsModalClose = () => {
    setShowDetailsModal(false);
    setEditDetails(null);
  };

  const onGenerateClick = (splashTemplateId) => {
    window.open(`/internal/organisations/${organisationId}/splash/templates/preview/${splashTemplateId}`, '_blank');
  };

  const onManageSocialSettings = (selectedTemplateId) => {
    const templateDetails = templates && templates.find(
      (template) => template.templateId === selectedTemplateId
    );

    setEditDetails(templateDetails);
    setTemplateId(selectedTemplateId);
    setShowManageSettingsModal(true);
  };

  const onManageSocialSettingsClose = () => {
    setShowManageSettingsModal(false);
    setTemplateId(null);
    setEditDetails(null);
  };

  const onElevateLinkModal = (selectedTemplateId) => {
    const templateDetails = templates && templates.find(
      (template) => template.templateId === selectedTemplateId
    );

    if (templateDetails) {
      setShowElevateLinkModal(true);
      setTemplateId(selectedTemplateId);
    }
  };

  const onElevateLinkModalClose = () => {
    setShowElevateLinkModal(false);
    setTemplateId(null);
  };

  const options = (selectedTemplateId, status, archivedAt) => [
  ...(!archivedAt && status === STATUS.PUBLISHED ? [
  {
    action: () => onUnPublish(selectedTemplateId),
    label:
    <Text success noMargin>
            Disable
          </Text>

  },
  {
    separator: true
  }] :
  []),
  ...(!archivedAt && status === STATUS.UNPUBLISHED ? [
  {
    action: () => onPublish(selectedTemplateId),
    label:
    <Text success noMargin>
            Enable
          </Text>

  },
  {
    separator: true
  }] :
  []),
  {
    action: () => onDetailsModal(selectedTemplateId),
    label: 'Splash Details'
  },
  {
    action: () => navigateToEditTemplate(selectedTemplateId),
    label: 'Edit Splash'
  },
  {
    action: () => onClone(selectedTemplateId),
    label: 'Duplicate'
  },
  {
    action: () => onManageSocialSettings(selectedTemplateId),
    label: 'Display Settings'
  },
  ...(isInternalAdmin ? [
  ...(status === STATUS.PUBLISHED ? [{
    label: 'Generate Preview',
    action: () => onGenerateClick(selectedTemplateId)
  }, { separator: true }] : [])] :
  [
  {
    action: () => onElevateLinkModal(selectedTemplateId),
    label: 'Link Elevate Template'
  }]),

  {
    separator: true
  },
  {
    action: () => archivedAt ? onUnarchive(selectedTemplateId) : onArchive(selectedTemplateId),
    label: archivedAt ? 'Unarchive' : 'Archive',
    isRed: true
  }];


  const columns = useMemo(() => getTemplatesDataTableColumns({
    options,
    onPublish,
    onUnPublish,
    isInternalAdmin,
    isMobile
  }), [templates, options, isInternalAdmin, isMobile]);

  if (!templates) {
    return null;
  }

  if (!templates.length && search) {
    return (
      <EmptyDataPlaceholder
        searchTerm={search} />);


  }

  if (!templates.length) {
    return <NoDataPlaceholder orgName={orgName} />;
  }

  return (
    <>
      <div className={cx('splash-page-data-table', { 'splash-page-data-table--no-header': !isInternalAdmin })}>
        <DataTable
          rows={templates}
          columns={columns}
          isLoading={isListLoading}
          columnFieldId="templateId" />

      </div>
      {isPublishVisible &&
      <FormModal
        visible={isPublishVisible}
        title="Are you sure?"
        onSave={onPublishSave}
        saveText="Enable Template"
        closeText="Cancel"
        onClose={onPublishModalClose}>

          <Text>
            Enabling this template will make it available for use within the selected
            Client Workspace.
          </Text>
        </FormModal>}

      {isUnpublishVisible &&
      <FormModal
        visible={isUnpublishVisible}
        title="Are you sure?"
        onSave={onUnpublishSave}
        saveText="Disable Template"
        closeText="Cancel"
        onClose={onUnpublishModalClose}>

          <Text>
            Reverting this template back to Draft will make it not available for use within
            the selected Client Workspace. All previously generated splash pages
            using this template will still be accessible.
          </Text>
        </FormModal>}

      {showCloneModal &&
      <FormModal
        visible={showCloneModal}
        title="Are you sure?"
        onSave={onCloneTemplate}
        saveText="Duplicate Template"
        closeText="Cancel"
        onClose={onCloneModalClose}>

          <Text>
            Duplicating this template will create another template.
          </Text>
        </FormModal>}

      {showDetailsModal &&
      <SplashDetails
        visible={showDetailsModal}
        title="Splash Details"
        templateId={templateId}
        onClose={onDetailsModalClose}
        action={STATUS.UPDATE}
        updateTemplate={updateTemplate}
        editDetails={editDetails}
        setTemplateDetails={setTemplateDetails} />}


      {showArchiveSplashModal &&
      <ArchiveSplashTemplate
        visible={showArchiveSplashModal}
        splashTemplateId={templateId}
        getTemplatesByOrg={getTemplatesByOrg}
        updatePitchSplashLink={updatePitchSplashLink}
        allSplashRelations={allSplashRelations}
        onClose={onArchiveModalClose}
        onArchiveSave={onArchiveSave} />}


      {showManageSettingsModal && templateId &&
      <UploaderConsumer>
          {({
          /* $FlowFixMe */
          addFilesToQueue,
          assetsMap
        }) =>
        <ManageSocialSettings
          visible={showManageSettingsModal}
          onClose={onManageSocialSettingsClose}
          addFilesToQueue={addFilesToQueue}
          assetsMap={assetsMap}
          organisationId={organisationId}
          updateAsset={updateAsset}
          updateTemplate={updateTemplate}
          templateId={templateId}
          templateDetails={editDetails}
          getPublicAssetUrl={getPublicAssetUrl}
          workspaceSocialSettings={workspaceSocialSettings} />}


        </UploaderConsumer>}


      {showElevateLinkModal &&
      <LinkElevateTemplates
        visible={showElevateLinkModal}
        title="Link Elevate Template"
        templateId={templateId}
        onClose={onElevateLinkModalClose}
        setTemplateDetails={setTemplateDetails}
        getElevateTemplatesByOrg={getElevateTemplatesByOrg}
        updatePitchSplashLink={updatePitchSplashLink}
        isInternalAdmin={isInternalAdmin} />}


    </>);

};

export default memo(SplashTemplateList);